import {
  createContext,
  FC,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import { logger } from "@moodys/mdc-frontend.services.logger";
import { EventBusTopic } from "@moodys/mdc-frontend.utils.event-bus";

import { ScrollLockItem, useLayoutContext } from "components/Layout";
import { initLiveChat } from "components/LiveChat/liveChat.helpers";
import { MENU_NAV } from "components/Menu/menu.constants";
import {
  FALLBACK_LANGUAGE_NODES,
  FALLBACK_MENU,
  MenuNodeType,
} from "components/Menu/model";
import { useAEMContent } from "hooks/useAEMContent/useAEMContent";
import { useEventBus } from "hooks/useEventBus";
import { fetchLiveChatInfo } from "services/apiClient/shellApiClient";
import { getPageLanguage } from "utils/language";

import { isLanguageContainerNode } from "./context.helpers";

export interface MenuContextProps {
  menuOpen: boolean;
  setMenuOpen: (open: boolean) => void;
  subMenuOpen: boolean;
  setSubMenuClose: VoidFunction;
  selectedMainMenuTitleKey?: string;
  setSelectedMainMenuTitleKey: (newTitleKey: string) => void;
  // this is meant to be a lock,
  // please only use if you're getting
  // user-event-driven race conditions
  menuTransitionLock: <T>(
    callback: () => T | Promise<T>
  ) => Promise<T | undefined>;
  menuModel: MenuNodeType.Container;
  subMenuModel?: MenuNodeType.Container;
  languageNodes: MenuNodeType.Link[];
}

const MenuContext = createContext<MenuContextProps | undefined>(undefined);

export const MenuContextProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [authenticated] = useEventBus(EventBusTopic.IsAuthenticated);
  const [, setLiveChat] = useEventBus(EventBusTopic.Shell.LiveChat);
  const menuModel = useAEMContent<MenuNodeType.Container>({
    contentUrl: MENU_NAV[getPageLanguage()],
    fallbackContent: FALLBACK_MENU,
  });
  const [menuOpen, setMenuOpen] = useState(false);
  const [selectedMainMenuTitleKey, setSelectedMainMenuTitleKey] =
    useState<string>();
  const menuTransitionLockRef = useRef<boolean>(false);
  const { addScrollLock, removeScrollLock } = useLayoutContext();
  const [subMenuModel, setSubMenuModel] = useState<MenuNodeType.Container>();
  const [languageNodes, setLanguageNodes] = useEventBus(
    EventBusTopic.Shell.AemMenu.LanguageNodes,
    FALLBACK_LANGUAGE_NODES
  );

  const handleSelectedMainMenuTitleKeyChange = (newTitleKey: string) => {
    if (newTitleKey === selectedMainMenuTitleKey) {
      setSelectedMainMenuTitleKey(undefined);
    } else {
      setSelectedMainMenuTitleKey(newTitleKey);
    }
  };

  const handleMenuOpen = (open: boolean) => {
    if (open) {
      setMenuOpen(true);
    } else {
      setMenuOpen(false);
      setSubMenuClose();
    }
  };

  const setSubMenuClose = () => {
    setSelectedMainMenuTitleKey(undefined);
  };

  useEffect(() => {
    if (authenticated) {
      fetchLiveChatInfo().then((liveChatInfo) => {
        if (liveChatInfo) {
          setLiveChat({
            isEnabled: liveChatInfo.enabled,
            isAvailable: false,
          });

          if (liveChatInfo.enabled) {
            initLiveChat({
              insiteId: liveChatInfo?.insiteId ?? "",
              handleChatAvailabilityState: setLiveChat,
            });
          }
        }
      });
    } else if (authenticated === false) {
      setLiveChat({
        isEnabled: false,
        isAvailable: false,
      });
    }
  }, [authenticated]);

  useEffect(() => {
    if (!selectedMainMenuTitleKey) {
      setSubMenuModel(undefined);
    } else {
      const subMenuNode = menuModel.childNodes?.find(
        (childNode) =>
          childNode.titleKey === selectedMainMenuTitleKey &&
          childNode.nodeType === "container"
      ) as MenuNodeType.Container | undefined;
      setSubMenuModel(subMenuNode);
    }
  }, [menuModel, selectedMainMenuTitleKey]);

  useEffect(() => {
    if (menuOpen) {
      addScrollLock(ScrollLockItem.MENU);
    } else {
      removeScrollLock(ScrollLockItem.MENU);
    }
  }, [addScrollLock, removeScrollLock, menuOpen]);

  useEffect(() => {
    const menuLanguageNodes = menuModel.childNodes?.find(
      isLanguageContainerNode
    )?.childNodes;
    setLanguageNodes(
      menuLanguageNodes?.length ? menuLanguageNodes : FALLBACK_LANGUAGE_NODES
    );
  }, [menuModel]);

  return (
    <MenuContext.Provider
      value={{
        menuOpen,
        setMenuOpen: handleMenuOpen,
        subMenuOpen: !!subMenuModel,
        setSubMenuClose,
        selectedMainMenuTitleKey,
        setSelectedMainMenuTitleKey: handleSelectedMainMenuTitleKeyChange,
        menuTransitionLock: async (callback) => {
          if (!menuTransitionLockRef.current) {
            menuTransitionLockRef.current = true;
            let value: ReturnType<typeof callback>;
            try {
              value = await callback();
            } finally {
              menuTransitionLockRef.current = false;
            }
            return value;
          }
        },
        menuModel,
        subMenuModel,
        languageNodes,
      }}
    >
      {children}
    </MenuContext.Provider>
  );
};

export const useMenu = () => {
  const context = useContext(MenuContext);
  if (!context) {
    logger.error({
      message:
        "No MenuContextProvider found. Did you forget to wrap your component in a MenuContextProvider?",
    });
    return {} as MenuContextProps;
  }
  return context;
};
